// Monthly presence calendar totals, shared by the totals row and the summary panel.
// Days come from hours (8h = 1 day), rounded to the nearest half day.

export const HOURS_PER_DAY = 8;

// Weekend gardes run from Friday evening to Monday morning, so Friday, Saturday and Sunday dates count
const WEEKEND_DUTY_DAYS = [5, 6, 0];

const roundToHalf = (value) => Math.round(value * 2) / 2;
const dayOfWeek = (dateString) => new Date(`${dateString}T00:00:00`).getDay();
export const dateKey = (date) => date.toISOString().split("T")[0];

function emptyTotal() {
  return {
    presenceDays: 0,
    fullDays: 0,
    halfDays: 0,
    hours: 0,
    absenceDays: 0,
    dutyShifts: 0,
    weekendDutyShifts: 0,
    openings: 0,
    byClinic: {},
    byClinicHours: {}
  };
}

// Totals per user id. With allClinics, presences at every clinic are counted, not just the current one.
export function buildTotals({ presences = [], absences = [], dutyShifts = [], clinicOpenings = [], clinicId, allClinics = false }) {
  const totals = {};
  const entry = (userId) => totals[userId] ??= emptyTotal();

  presences
    .filter((presence) => allClinics || presence.clinic_id === clinicId)
    .forEach((presence) => {
      const total = entry(presence.user.id);
      const hours = parseFloat(presence.hours || 0);
      total.hours += hours;
      total.presenceDays += hours / HOURS_PER_DAY;
      presence.period === "day" ? total.fullDays += 1 : total.halfDays += 1;

      const clinicName = presence.clinic?.name ?? "";
      total.byClinic[clinicName] = (total.byClinic[clinicName] ?? 0) + hours / HOURS_PER_DAY;
      total.byClinicHours[clinicName] = (total.byClinicHours[clinicName] ?? 0) + hours;
    });

  absences.forEach((absence) => entry(absence.user.id).absenceDays += absence.period === "day" ? 1 : 0.5);

  dutyShifts.forEach((shift) => shift.vets.forEach((vet) => {
    const total = entry(vet.id);
    total.dutyShifts += 1;
    if (WEEKEND_DUTY_DAYS.includes(dayOfWeek(shift.date))) total.weekendDutyShifts += 1;
  }));

  clinicOpenings.forEach((opening) => entry(opening.user.id).openings += 1);

  Object.values(totals).forEach((total) => {
    total.presenceDays = roundToHalf(total.presenceDays);
    Object.keys(total.byClinic).forEach((name) => total.byClinic[name] = roundToHalf(total.byClinic[name]));
  });

  return totals;
}

export function totalsFor(totals, user) {
  return totals[user.id] ?? emptyTotal();
}

// Dates in the month with no vet on garde
export function uncoveredDutyDates(dates, dutyShifts) {
  const covered = new Set(dutyShifts.filter((shift) => shift.vets.length > 0).map((shift) => shift.date));
  return dates.map(dateKey).filter((date) => !covered.has(date));
}

// Dates in the month (Monday to Saturday) with nobody assigned to open the clinic
export function uncoveredOpeningDates(dates, clinicOpenings) {
  const covered = new Set(clinicOpenings.map((opening) => opening.date));
  return dates.map(dateKey).filter((date) => dayOfWeek(date) !== 0 && !covered.has(date));
}

export function formatNumber(value) {
  return value.toLocaleString("fr");
}

// French plural: 0, 0,5, 1 and 1,5 stay singular
export function pluralize(value, word) {
  return `${formatNumber(value)} ${word}${value >= 2 ? "s" : ""}`;
}
